import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {FUITree, FUITreesComponent} from "@fedeltapointofsale/ui";
import {GroupedOutletCustomData} from "../grouped-outlet-custom.data";
import {TranslateService} from "@ngx-translate/core";
import {GroupedOutletCustomSearchModel} from "../grouped-outlet-custom-search.model";
import {Subscription} from "rxjs";

export enum ItemType {
    GROUPS,
    LOCATIONS,
    OUTLET_GROUP,
    OUTLET,
    CUSTOM_OBJECT
}

class TreeContentData {
    constructor(public itemType: ItemType, public outletCustomData?: GroupedOutletCustomData) {
    }
}


/**
 * Localisation outlet selection component that displays a tree of outlets and outlet groups for user selection.
 * A higher level component must provide the GroupedOutletCustomSearchModel so the outlets can be retrieved.
 *
 * Selected outlets and custom outlet group selections may be provided as inputs.
 *
 * This component may be paired with a LocalisationOutletSelectionAutocompleteComponent to allow the user to select
 * outlets via an autocomplete selection.
 */
@Component({
    selector: "wbo-localisation-outlet-selection",
    templateUrl: "./localisation-outlet-selection.component.html",
    styleUrls: ["./localisation-outlet-selection.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocalisationOutletSelectionComponent implements OnInit, OnDestroy, OnChanges {

    private _selectedOutlets = [];
    private _selectedCustomObjects = [];
    @Input() readonly: boolean;
    @Output() selectionChanged: EventEmitter<any> = new EventEmitter();

    unselectedTreesData: FUITree[];
    selectedTreesData: FUITree[];

    @ViewChild("unselectedTreeComponent") unselectedTreeComponent: FUITreesComponent;
    @ViewChild("selectedTreeComponent") selectedTreeComponent: FUITreesComponent;

    private searchSubscription: Subscription;

    hasLoadedSearchData: boolean;

    constructor(public searchModel: GroupedOutletCustomSearchModel,
                private translateService: TranslateService,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    get selectedOutlets(): any[] {
        return this._selectedOutlets;
    }

    @Input()
    set selectedOutlets(value: any[]) {
        this._selectedOutlets = value || [];

        if (this.hasLoadedSearchData) {
            this.buildTrees();
        }
    }

    get selectedCustomObjects(): any[] {
        return this._selectedCustomObjects;
    }

    @Input()
    set selectedCustomObjects(value: any[]) {
        this._selectedCustomObjects = value || [];
        if (this.hasLoadedSearchData) {
            this.buildTrees();
        }
    }

    ngOnInit(): void {
        if (!this._selectedOutlets) {
            this._selectedOutlets = [];
        }

        if (!this._selectedCustomObjects) {
            this._selectedCustomObjects = [];
        }

        this.searchSubscription = this.searchModel.$data.subscribe(() => {
            this.buildTrees();

            this.hasLoadedSearchData = true;
        });

    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    buildTrees(): void {
        this.unselectedTreesData = this.buildTree(false);
        this.selectedTreesData = this.buildTree(true);
        this.changeDetectorRef.markForCheck();
    }


    /**
     * Build the tree selecting the items that are selected or unselected depending on the selected parameter
     * @param {boolean} selected
     * @returns {Array<FUITree>}
     */
    buildTree(selected: boolean): Array<FUITree> {
        const tree = [];

        const customObjectNode: FUITree = new FUITree({
            title: "",
            id: "Groups",
            open: true,
            data: new TreeContentData(ItemType.GROUPS)
        });
        this.translateService.get("wbo.localisation.heading.customObject").subscribe(translation => {
            customObjectNode.title = translation
        });

        const locationsNode: FUITree = new FUITree({
            title: "",
            id: "Locations",
            open: true,
            data: new TreeContentData(ItemType.LOCATIONS)
        });
        this.translateService.get("wbo.localisation.heading.outletGroup").subscribe(translation => {
            locationsNode.title = translation
        });

        let currentGroup: string;
        let currentNode: FUITree;
        let groupIncluded: boolean;
        this.searchModel.data.forEach(item => {
            if (item.type === GroupedOutletCustomData.TYPE_OUTLET) {
                if (currentGroup !== item.group) {
                    currentNode = new FUITree({
                        id: this.buildTreeId(ItemType.OUTLET_GROUP, item.group), title: item.group, icon: "folder",
                        data: new TreeContentData(ItemType.OUTLET_GROUP)
                    });
                    groupIncluded = false;
                    currentGroup = item.group;
                }

                const outletSelected: boolean = this._selectedOutlets.indexOf(item.id) > -1;
                const include: boolean = selected ? outletSelected : !outletSelected;

                if (include) {
                    const node: FUITree = new FUITree({
                        id: this.buildTreeId(ItemType.OUTLET, item.id), title: item.name, icon: "marker",
                        data: new TreeContentData(ItemType.OUTLET, item)
                    });
                    currentNode.addChildNode(node);
                    if (!groupIncluded) {
                        locationsNode.addChildNode(currentNode);
                        groupIncluded = true;
                    }
                }
            } else if (item.type === GroupedOutletCustomData.TYPE_CUSTOM_OBJECT) {
                const customObjectSelected: boolean = this._selectedCustomObjects.indexOf(item.id) > -1;

                const include: boolean = selected ? customObjectSelected : !customObjectSelected;

                if (include) {
                    const node: FUITree = new FUITree({
                        id: this.buildTreeId(ItemType.CUSTOM_OBJECT, item.id),
                        title: item.name, icon: "folder",
                        data: new TreeContentData(ItemType.CUSTOM_OBJECT, item)
                    });
                    customObjectNode.addChildNode(node);
                }
            }
        });

        if (customObjectNode.trees.length > 0) {
            tree.push(customObjectNode);
        }
        if (locationsNode.trees.length > 0) {
            tree.push(locationsNode);
        }

        return tree;
    }


    buildTreeId(itemType: ItemType, id: any) {
        if (itemType === ItemType.OUTLET_GROUP) {
            return "OUTLET_GROUP_" + id;
        } else if (itemType === ItemType.OUTLET) {
            return "OUTLET_" + id;
        } else {
            return "CUSTOM_OBJECT_" + id;
        }
    }


    /**
     *
     */
    resetSelectedOutlets(): void {
        this._selectedOutlets.splice(0, this._selectedOutlets.length);
        this._selectedCustomObjects.splice(0, this._selectedCustomObjects.length);
        this.buildTrees();
        this.selectionChanged.emit();
    }

    /**
     * Select or deselect an item
     * @param {FUITree} item
     * @param {boolean} select
     */
    selectItem(item: FUITree, select: boolean): void {
        this.processSelectItem(item, select);
        const targetTreeComponent: FUITreesComponent = select ? this.selectedTreeComponent : this.unselectedTreeComponent;
        // if the item has a parent toggle it
        if (item.parent) {
            targetTreeComponent.toggleItem(item.parent || item, true);
        }
        // if the item is group toggle false
        if (!item.data.outletCustomData) {
            targetTreeComponent.toggleItem(item, false);
        }
        this.buildTrees();
        this.selectionChanged.emit();
    }

    /**
     *
     * @param item
     * @param select
     */
    processSelectItem(item: FUITree, select: boolean): void {
        if (item.data.outletCustomData) {
            const groupedOutletCustomData: GroupedOutletCustomData = item.data.outletCustomData;
            const ids: Array<number> = groupedOutletCustomData.type === GroupedOutletCustomData.TYPE_OUTLET ? this._selectedOutlets : this._selectedCustomObjects;
            if (select) {
                ids.push(groupedOutletCustomData.id);
            } else {
                ids.splice(ids.indexOf(groupedOutletCustomData.id), 1);
            }
        } else {
            item.trees.forEach(child => this.processSelectItem(child, select));
        }
    }


    /**
     * Find a item in the tree
     * @param {FUITree[]} data
     * @param {GroupedOutletCustomData} item
     * @returns {FUITree}
     */
    findTreeItem(data: FUITree[], id: string): FUITree {
        let result: FUITree = undefined;
        data.find(i => {
            result = (i.id === id) ? i : this.findTreeItem(i.trees, id);
            return result !== undefined;
        });
        return result;
    }

    /**
     * Indicates if an item in the panel is selected
     * @param id Item Id
     * @param type Type of Item
     */
    isSelected(id: any, type: ItemType): boolean {
        if (type === ItemType.OUTLET_GROUP) {
            const selectedTreeItem: FUITree = this.findTreeItem(this.unselectedTreesData, this.buildTreeId(type, id));
            return selectedTreeItem === undefined;
        } else if (type === ItemType.OUTLET) {
            return this._selectedOutlets.indexOf(id) > -1;
        } else {
            return this._selectedCustomObjects.indexOf(id) > -1;
        }
    }

    /**
     * Toggle the selection of an item
     * @param id Item Id
     * @param type Type of Item
     */
    toggleSelection(id: any, type: ItemType): void {
        const unselectedTreeItem: FUITree = this.findTreeItem(this.unselectedTreesData, this.buildTreeId(type, id));
        const selectedTreeItem: FUITree = this.findTreeItem(this.selectedTreesData, this.buildTreeId(type, id));

        if (unselectedTreeItem) {
            this.selectItem(unselectedTreeItem, true);
        } else {
            this.selectItem(selectedTreeItem, false);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["_selectedOutlets"] || changes["_selectedCustomObjects"]) {
            this.buildTrees();
        }
    }
}
